//@ts-ignore
import { QueryEMX2 } from "molgenis-components";
import { gql, request } from "graphql-request";
import {
  IFilterFacet,
  IFilterOption,
  IOntologyItem,
} from "../interfaces/interfaces";
import { useFiltersStore } from "../stores/filtersStore";

/** Configurable array of values to filter out, for example 'Other, unknown' that make no sense to the user. */
function removeOptions(
  filterOptions: IFilterOption[],
  filterFacet: IFilterFacet
) {
  const optionsToRemove = filterFacet.removeOptions;

  if (!optionsToRemove || !optionsToRemove.length) return filterOptions;

  optionsToRemove.map((option: any) => option.toLowerCase());
  return filterOptions.filter(
    (filterOption: IFilterOption) =>
      !optionsToRemove.includes(filterOption.text.toLowerCase())
  );
}

export const customFilterOptions = (filterFacet: IFilterFacet) => {
  const filtersStore = useFiltersStore();
  const { facetIdentifier, customOptions } = filterFacet;
  return () =>
    new Promise((resolve) => {
      if (!facetIdentifier) {
        resolve([]);
        return;
      }
      const options = filtersStore.getFilterOptions(facetIdentifier);

      if (!options.length) {
        filtersStore.setFilterOptions(facetIdentifier, customOptions);
        resolve(customOptions);
      } else {
        resolve(customOptions);
      }
    });
};

function _mapToOptions(
  row: Record<string, any>,
  filterLabelAttribute: string,
  filterValueAttribute: string,
  extraAttributes?: string[]
): Record<string, any> {
  if (extraAttributes?.length) {
    return {
      text: row[filterLabelAttribute],
      value: row[filterValueAttribute],
      extraAttributes: getExtraAttributes(row, extraAttributes),
    };
  } else {
    return {
      text: row[filterLabelAttribute],
      value: row[filterValueAttribute],
    };
  }
}

function getExtraAttributes(row: Record<string, any>, attributes: string[]) {
  return attributes.reduce((accum: Record<string, any>, attribute: string) => {
    if (attribute.includes(".")) {
      const [parent, child] = attribute.split(".");
      accum[attribute] = row[parent][child];
    } else {
      accum[attribute] = row[attribute];
    }
    return accum;
  }, {});
}

export const genericFilterOptions = (filterFacet: IFilterFacet) => {
  const filtersStore = useFiltersStore();
  const {
    sourceTable,
    sourceSchema,
    facetIdentifier,
    filterLabelAttribute,
    filterValueAttribute,
    extraAttributes,
    sortColumn,
    sortDirection,
  } = filterFacet;

  return () =>
    new Promise((resolve) => {
      if (
        !facetIdentifier ||
        !filterLabelAttribute ||
        !filterValueAttribute ||
        !sourceTable
      ) {
        resolve([]);
        return;
      }
      const filterOptions = filtersStore.getFilterOptions(facetIdentifier);
      const selection = getAttributeSelection(
        filterLabelAttribute,
        filterValueAttribute,
        extraAttributes
      );

      if (!filterOptions.length) {
        new QueryEMX2(getSchema(sourceSchema))
          .table(sourceTable)
          .select(selection)
          .orderBy(sourceTable, sortColumn, sortDirection)
          .execute()
          .then((response: Record<string, Record<string, any>>) => {
            const newOptions = response[sourceTable].map(
              (row: Record<string, any>) => {
                const result = _mapToOptions(
                  row,
                  filterLabelAttribute,
                  filterValueAttribute,
                  extraAttributes
                );
                const parent = row.parent
                  ? row.parent[filterValueAttribute]
                  : undefined;
                if (parent) {
                  result[parent] = parent;
                }
                const children = row.children?.map(
                  (child: any) => child[filterValueAttribute]
                );
                if (children) {
                  result[children] = children;
                }
                return result;
              }
            );

            const filteredOptions = removeOptions(newOptions, filterFacet);
            filtersStore.setFilterOptions(facetIdentifier, filteredOptions);
            resolve(filteredOptions);
          });
      } else {
        resolve(filterOptions);
      }
    });
};

function getAttributeSelection(
  filterLabelAttribute: string,
  filterValueAttribute: string,
  extraAttributes?: string[]
) {
  if (extraAttributes?.length) {
    return [filterLabelAttribute, filterValueAttribute, ...extraAttributes];
  } else {
    return [filterLabelAttribute, filterValueAttribute];
  }
}

/** Options for a plain (non-REF) column: distinct values pulled live from the
 * main table via a GraphQL `_groupBy` query, rather than from a separate
 * lookup table. The field name is the last segment of `applyToColumn`
 * (e.g. "services.unitTheme" -> "unitTheme"), since label and value are the
 * same string for a scalar column. */
export const groupByFilterOptions = (filterFacet: IFilterFacet) => {
  const filtersStore = useFiltersStore();
  const { sourceTable, sourceSchema, facetIdentifier, applyToColumn, sortDirection } =
    filterFacet;
  const field = applyToColumn?.split(".").pop();

  return () =>
    new Promise((resolve) => {
      if (!facetIdentifier || !sourceTable || !field) {
        resolve([]);
        return;
      }

      const filterOptions = filtersStore.getFilterOptions(facetIdentifier);

      if (filterOptions.length) {
        resolve(filterOptions);
        return;
      }

      request(
        getSchema(sourceSchema),
        gql`{ ${sourceTable}_groupBy { count ${field} } }`
      ).then((response: Record<string, Record<string, any>[]>) => {
        const rows = response[`${sourceTable}_groupBy`] || [];
        const newOptions = rows
          .filter(
            (row) => row[field] !== null && row[field] !== undefined && row[field] !== ""
          )
          .map((row) => ({ text: row[field], value: row[field] }));

        newOptions.sort((a, b) =>
          sortDirection?.toLowerCase() === "desc"
            ? b.text.localeCompare(a.text)
            : a.text.localeCompare(b.text)
        );

        const filteredOptions = removeOptions(newOptions, filterFacet);
        filtersStore.setFilterOptions(facetIdentifier, filteredOptions);
        resolve(filteredOptions);
      });
    });
};

export const ontologyFilterOptions = (filterFacet: IFilterFacet) => {
  const filtersStore = useFiltersStore();
  const {
    ontologyIdentifiers,
    sourceSchema,
    sourceTable,
    facetIdentifier,
    filterLabelAttribute,
    filterValueAttribute,
    sortColumn,
    sortDirection,
  } = filterFacet;

  return () =>
    new Promise((resolve) => {
      if (!facetIdentifier || !sourceTable) {
        resolve([]);
        return;
      }
      const options = filtersStore.getFilterOptions(facetIdentifier);

      const selection = [
        filterLabelAttribute,
        filterValueAttribute,
        "code",
        `parent.${filterValueAttribute}`,
      ];

      if (!options.length) {
        /** make it query after all the others, saves 50% of initial load */
        const waitAfterBiobanks = setTimeout(() => {
          new QueryEMX2(getSchema(sourceSchema))
            .table(sourceTable)
            .select(selection)
            .orderBy(sourceTable, sortColumn, sortDirection)
            .execute()
            .then((response: any) => {
              const itemsSplitByOntology = getItemsSplitByOntology(
                response[sourceTable],
                ontologyIdentifiers || []
              );

              filtersStore.setFilterOptions(
                facetIdentifier,
                itemsSplitByOntology
              );
              resolve(itemsSplitByOntology);
            });
          clearTimeout(waitAfterBiobanks);
        }, 1000);
      } else {
        resolve(options);
      }
    });
};

function getItemsSplitByOntology(
  ontologyItems: IOntologyItem[],
  ontologyIdentifiers: string[]
): Record<string, IOntologyItem[]> {
  const childrenPerParent = getChildrenPerParent(ontologyItems);
  const itemsWithChildren = getItemsWithChildren(
    ontologyItems,
    childrenPerParent
  );

  const rootNodes = itemsWithChildren.filter(
    (item: any) => !item.parent?.length
  );

  const itemsSplitByOntology = splitItemsByOntology(
    rootNodes,
    ontologyIdentifiers
  );

  return { ...itemsSplitByOntology };
}

function getItemsWithChildren(
  items: IOntologyItem[],
  childrenPerParent: Record<string, IOntologyItem[]>
) {
  return items.map((item) => {
    if (childrenPerParent[item.name]) {
      item.children = childrenPerParent[item.name];
    }
    return item;
  });
}

function splitItemsByOntology(
  rootNodes: IOntologyItem[],
  ontologyIdentifiers: string[]
): Record<string, IOntologyItem[]> {
  const itemsSplitByOntology: Record<string, IOntologyItem[]> = {};
  for (const ontologyItem of rootNodes) {
    for (const ontologyId of ontologyIdentifiers) {
      if (ontologyItem.name.toLowerCase().includes(ontologyId.toLowerCase())) {
        if (!itemsSplitByOntology[ontologyId]) {
          itemsSplitByOntology[ontologyId] = [ontologyItem];
        } else {
          itemsSplitByOntology[ontologyId].push(ontologyItem);
        }
      }
    }
  }
  return itemsSplitByOntology;
}

function getChildrenPerParent(
  items: IOntologyItem[]
): Record<string, IOntologyItem[]> {
  let childrenPerParent: Record<string, IOntologyItem[]> = {};
  items.forEach((item) => {
    item.parent?.forEach((parent) => {
      if (childrenPerParent[parent.name]) {
        childrenPerParent[parent.name].push(item);
      } else {
        childrenPerParent[parent.name] = [item];
      }
    });
  });
  return childrenPerParent;
}

export function getSchema(sourceSchema: string | undefined) {
  return sourceSchema
    ? `${window.location.protocol}//${window.location.host}/${sourceSchema}`
    : "graphql";
}
